import React from 'react'
import { CircularProgress, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Unavailable from '../../pages/Unavailable'
import { UserApplicationData } from '../../store/userManagement/userManagementSearchService'
import UserManagementNoRecords from './UserManagementNoRecords'
import UserManagementTable from './UserManagementTable'
import { Styles } from '../../types'
import RsmPagination, {
  RsmPaginationPageSizes,
} from '../../rsmCoreComponents/components/RsmPagination/RsmPagination'

interface UserManagementResultsProps {
  usersData: UserApplicationData[] | undefined
  isUsersError: boolean
  pageNumber: number
  pageSize: RsmPaginationPageSizes
  onPageChange: (pageNumber: number, pageSize: RsmPaginationPageSizes) => void
  onSorting: (sortBy: string, isAscending: 'asc' | 'desc') => void
  sortColumn: string
  sortDirection: 'asc' | 'desc'
}

const styles: Styles = {
  container: () => ({
    display: 'flex',
    justifyContent: 'center',
    verticalAlign: 'middle',
    height: '2rem',
    marginTop: '2rem',
  }),
}

const UserManagementResults = ({
  usersData,
  isUsersError,
  pageNumber,
  pageSize,
  onPageChange,
  onSorting,
  sortColumn,
  sortDirection,
}: UserManagementResultsProps) => {
  const { t } = useTranslation()

  // Error state - show unavailable page with refresh option
  if (isUsersError) {
    return <Unavailable />
  }

  // Empty state - show no records found with call-to-action
  if (usersData && usersData.length === 0) {
    return <UserManagementNoRecords />
  }

  // Data state - show user table with pagination
  if (usersData) {
    // Calculate total rows for pagination (using array length for now)
    const totalRows = usersData.length

    return (
      <>
        {/* User Management Table - displays user data with sortable columns, pagination, and action menus */}
        <UserManagementTable
          usersData={usersData}
          onSort={onSorting}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          pageNumber={pageNumber}
          pageSize={pageSize}
        />

        {/* Pagination Component */}
        <RsmPagination
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalRows={totalRows}
          // MUI Select returns pageSize as a string; convert to number to keep pagination logic consistent
          onPageChange={(page, size) => {
            const parsedSize = Number(size) as RsmPaginationPageSizes
            onPageChange(page, parsedSize)
          }}
        />
      </>
    )
  }

  // Loading state - show spinner while data is being fetched
  return (
    <Box sx={styles.container} aria-busy="true">
      <CircularProgress
        color="secondary"
        data-testid="Spn_UserManagement_Loading"
        aria-label={t('UserManagement.LoadingUsers', 'Loading users')}
      />
    </Box>
  )
}

export default UserManagementResults
